const chatApiUrl = process.env.REACT_APP_CHAT_API_URL || "http://localhost:7000";
const browserOrigin = typeof window !== "undefined" ? window.location.origin : "";
const browserWsOrigin = browserOrigin.replace(/^https:/, "wss:").replace(/^http:/, "ws:");
const authApiUrl = process.env.REACT_APP_AUTH_API_URL || browserOrigin || "http://localhost:8000";
const resolvedChatApiUrl = process.env.REACT_APP_CHAT_API_URL || browserOrigin || chatApiUrl;
const resolvedCrmApiUrl = process.env.REACT_APP_CRM_API_URL || browserOrigin || resolvedChatApiUrl;
const defaultChatWsUrl = process.env.REACT_APP_CHAT_WS_URL || browserWsOrigin || resolvedChatApiUrl.replace(/^https:/, "wss:").replace(/^http:/, "ws:");
const authApiPrefix = process.env.REACT_APP_AUTH_API_PREFIX || "/api/auth";
const chatApiPrefix = process.env.REACT_APP_CHAT_API_PREFIX || "/api/chat";
const crmApiPrefix = process.env.REACT_APP_CRM_API_PREFIX || "/api/crm";

const joinPath = (prefix, path = "") => {
    const normalizedPrefix = prefix.endsWith("/") ? prefix.slice(0, -1) : prefix;
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    return `${normalizedPrefix}${normalizedPath}`;
};

const authPath = (path) => joinPath(authApiPrefix, path);
const chatPath = (path) => joinPath(chatApiPrefix, path);
const crmPath = (path) => joinPath(crmApiPrefix, path);

const apiRoutes = {
    BASE_URL: authApiUrl,
    CHAT_URL: resolvedChatApiUrl,
    CRM_URL: resolvedCrmApiUrl,
    CHAT_WS_URL: defaultChatWsUrl,
    auth: {
        register: authPath("/register"),
        login: authPath("/login"),
        logout: authPath("/logout"),
        refreshToken: authPath("/refresh-token"),
        google: authPath("/auth/google"),
        googleStatus: authPath("/auth/google/status"),
    },
    user: {
        info: authPath("/user-info"),
        search: authPath("/search-user"),
        addContact: authPath("/add-contact"),
        removeContact: authPath("/remove-contact"),
        incomingRequests: authPath("/pending-requests"),
        assignableUsers: authPath("/users/assignable"),
        employees: authPath("/users/employees"),
        presencePing: authPath("/presence/ping"),
    },
    admin: {
        pendingUsers: authPath("/admin/pending-users"),
        approveUser: (userId) => authPath(`/admin/users/${userId}/approve`),
    },
    chat: {
        createChat: chatPath("/create_chat"),
        invitedChats: (username) => chatPath(`/invited/${username}`),
        acceptInvitation: (chatId) => chatPath(`/accept_invitation/${chatId}`),
        calls: chatPath("/calls"),
        callLogs: chatPath("/call-logs"),
        iceServers: chatPath("/ice-servers"),
        incomingCalls: chatPath("/calls/incoming"),
        callDetails: (callId) => chatPath(`/calls/${callId}`),
        acceptCall: (callId) => chatPath(`/calls/${callId}/accept`),
        declineCall: (callId) => chatPath(`/calls/${callId}/decline`),
        groupCalls: chatPath("/group-calls"),
        incomingGroupCalls: chatPath("/group-calls/incoming"),
        groupCallDetails: (groupCallId) => chatPath(`/group-calls/${groupCallId}`),
        joinGroupCall: (groupCallId) => chatPath(`/group-calls/${groupCallId}/join`),
        declineGroupCall: (groupCallId) => chatPath(`/group-calls/${groupCallId}/decline`),
        leaveGroupCall: (groupCallId) => chatPath(`/group-calls/${groupCallId}/leave`),
        callSocket: (roomId, username) =>
            chatPath(`/ws/calls/${encodeURIComponent(roomId)}/${encodeURIComponent(username)}`),
        groupCallSocket: (roomId, username) =>
            chatPath(`/ws/group-calls/${encodeURIComponent(roomId)}/${encodeURIComponent(username)}`),
        notificationsSocket: (username) =>
            chatPath(`/ws/notifications/${encodeURIComponent(username)}`),
    },
    crm: {
        summary: crmPath("/summary"),
        reports: crmPath("/reports"),
        search: crmPath("/search"),
        companies: crmPath("/companies"),
        companyDetails: (companyId) => crmPath(`/companies/${companyId}`),
        companyTimeline: (companyId) => crmPath(`/companies/${companyId}/timeline`),
        companyArchive: (companyId) => crmPath(`/companies/${companyId}/archive`),
        contacts: crmPath("/contacts"),
        contactDetails: (contactId) => crmPath(`/contacts/${contactId}`),
        contactArchive: (contactId) => crmPath(`/contacts/${contactId}/archive`),
        activities: crmPath("/activities"),
        activityDetails: (activityId) => crmPath(`/activities/${activityId}`),
        activityArchive: (activityId) => crmPath(`/activities/${activityId}/archive`),
        leads: crmPath("/leads"),
        leadDetails: (leadId) => crmPath(`/leads/${leadId}`),
        leadConvert: (leadId) => crmPath(`/leads/${leadId}/convert`),
        leadArchive: (leadId) => crmPath(`/leads/${leadId}/archive`),
        azsRequestStatus: (publicNumber) => crmPath(`/azs/requests/${encodeURIComponent(publicNumber)}/status`),
        deals: crmPath("/deals"),
        dealDetails: (dealId) => crmPath(`/deals/${dealId}`),
        dealArchive: (dealId) => crmPath(`/deals/${dealId}/archive`),
        tasks: crmPath("/tasks"),
        taskDetails: (taskId) => crmPath(`/tasks/${taskId}`),
        taskArchive: (taskId) => crmPath(`/tasks/${taskId}/archive`),
        taskStatus: (taskId) => crmPath(`/tasks/${taskId}/status`),
        recordAttachments: (entityType, entityId) =>
            crmPath(`/attachments/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`),
        recordAttachmentDownload: (entityType, entityId, attachmentId) =>
            crmPath(`/attachments/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}/${attachmentId}/download`),
        recordAttachment: (entityType, entityId, attachmentId) =>
            crmPath(`/attachments/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}/${attachmentId}`),
        siteTasks: crmPath("/site-tasks"),
        siteTaskCounters: crmPath("/site-tasks/counters"),
        siteTask: (taskId) => crmPath(`/site-tasks/${taskId}`),
        siteTaskArchive: (taskId) => crmPath(`/site-tasks/${taskId}/archive`),
        siteTaskStatus: (taskId) => crmPath(`/site-tasks/${taskId}/status`),
        siteTaskChecklist: (taskId) => crmPath(`/site-tasks/${taskId}/checklist`),
        siteTaskComments: (taskId) => crmPath(`/site-tasks/${taskId}/comments`),
        siteTaskEvents: (taskId) => crmPath(`/site-tasks/${taskId}/events`),
        siteTaskAttachments: (taskId) => crmPath(`/site-tasks/${taskId}/attachments`),
        siteTaskAttachmentDownload: (taskId, attachmentId) =>
            crmPath(`/site-tasks/${taskId}/attachments/${attachmentId}/download`),
        siteTaskAttachment: (taskId, attachmentId) =>
            crmPath(`/site-tasks/${taskId}/attachments/${attachmentId}`),
        priceMonitorProducts: crmPath("/price-monitor/products"),
        priceMonitorProduct: (productCode) => crmPath(`/price-monitor/products/${encodeURIComponent(productCode)}`),
        priceMonitorFavorite: (productCode) => crmPath(`/price-monitor/products/${encodeURIComponent(productCode)}/favorite`),
        priceMonitorOwnPriceRefresh: (productCode) =>
            crmPath(`/price-monitor/products/${encodeURIComponent(productCode)}/our-price/refresh`),
        priceMonitorOwnPricesRefreshAll: crmPath("/price-monitor/our-prices/refresh"),
        priceMonitorOwnPricesRefreshStatus: crmPath("/price-monitor/our-prices/refresh-status"),
        priceMonitorCompetitor: (productCode, competitorName) =>
            crmPath(`/price-monitor/products/${encodeURIComponent(productCode)}/competitors/${encodeURIComponent(competitorName)}`),
        priceMonitorCompetitorVerification: (productCode, competitorName) =>
            crmPath(`/price-monitor/products/${encodeURIComponent(productCode)}/competitors/${encodeURIComponent(competitorName)}/verification`),
        priceMonitorDifferenceReview: (productCode, competitorName) =>
            crmPath(`/price-monitor/products/${encodeURIComponent(productCode)}/competitors/${encodeURIComponent(competitorName)}/difference-review`),
        priceMonitorCompetitorRefresh: (productCode, competitorName) =>
            crmPath(`/price-monitor/products/${encodeURIComponent(productCode)}/competitors/${encodeURIComponent(competitorName)}/refresh`),
        priceMonitorCompetitorRefreshAll: (competitorName) =>
            crmPath(`/price-monitor/competitors/${encodeURIComponent(competitorName)}/refresh`),
        priceMonitorCompetitorRefreshStatus: (competitorName) =>
            crmPath(`/price-monitor/competitors/${encodeURIComponent(competitorName)}/refresh-status`),
        priceMonitorCompetitorFeed: (competitorName) =>
            crmPath(`/price-monitor/competitors/${encodeURIComponent(competitorName)}/feed`),
        priceMonitorConfigExport: crmPath("/price-monitor/config/export"),
        priceMonitorConfigImport: crmPath("/price-monitor/config/import"),
        priceMonitorExcelExport: crmPath("/price-monitor/excel/export"),
        priceMonitorExcelImport: crmPath("/price-monitor/excel/import"),
        priceMonitorImport: crmPath("/price-monitor/import"),
        catalogImportYml: crmPath("/catalog-import/yml"),
        catalogImportRuns: crmPath("/catalog-import/runs"),
        catalogProductSearch: crmPath("/catalog-products/search"),
        health: crmPath("/health"),
    },
};

export default apiRoutes;
